import { Invoice, InvoiceLine, InvoiceLineAllocation, ServiceItem, ServiceTransaction } from '../types';
import { asDay, berlinerSortierzeit, buchungstag, isPostingEntry, kennungFuerVergleich } from './serviceLogUtils';

/**
 * Zuordnung zwischen Rechnungszeilen und den Eintraegen des
 * Leistungsprotokolls.
 *
 * DAS PROBLEM, DAS DIESE DATEI LOEST: Das Leistungsverzeichnis merkt sich je
 * Leistung nur eine Summe — quantityBilled — und NICHT, welche erbrachte
 * Leistung damit bezahlt ist. Jede datumsbezogene Aussage musste deshalb
 * raten: welcher Monat noch offen ist, ob ein Zeitraumfilter eine bereits
 * abgerechnete Begehung erneut anbietet, was eine kumulative Rechnung zum
 * Stichtag zeigt. Die Annahme "abgerechnet wird von alt nach neu" ist
 * plausibel, aber nirgends belegt.
 *
 * Statt sie immer wieder neu zu treffen, wird die Zuordnung EINMAL getroffen —
 * beim Ausstellen, wo Zeitraum und Mengen endgueltig feststehen — und im Beleg
 * festgeschrieben. Ab da ist sie eine Tatsache, keine Annahme.
 *
 * Pure Funktionen, kein Supabase, kein Date.now().
 */

/** Unterhalb dieser Menge gilt eine Position als leer — Rundungsrauschen. */
const EPS = 0.0005;

/** Ein Protokolleintrag, der abgerechnet werden kann. */
export interface AllocatableEntry {
    id: string;
    serviceId: string;
    /** Leistungsdatum. Fehlt es, gehoert der Eintrag zu keinem Zeitraum. */
    date?: string;
    quantity: number;
}

/** Ein Eintrag mit dem, was von ihm noch offen ist. */
export interface OpenEntry extends AllocatableEntry {
    openQty: number;
}

/**
 * Die Eintraege, die ueberhaupt fuer eine Abrechnung in Frage kommen.
 *
 * Draussen bleiben: Buchungseintraege (die Rueckschreibung einer Rechnung ist
 * keine Arbeit), Eintraege ohne Leistungsbezug und Korrekturen nach unten.
 * Eine negative Korrektur hat keine eigene abrechenbare Menge — sie mindert
 * den Gesamtstand, aus dem hier verteilt wird.
 *
 * Buchung heisst hier BEIDES: die Rueckschreibung einer Rechnung ('BU-…') und
 * die manuelle Verbuchung. Frueher stand hier nur `isAccountingEntry`, und
 * eine manuelle Verbuchung ueber 7 Stueck zaehlte als sieben Begehungen —
 * genug, damit der bereits abgerechnete Bestand in der Monatsuebersicht sieben
 * Eintraege zu frueh aufgezehrt war und offene Begehungen zwei Monate zu weit
 * vorn erschienen (Projekt 5606).
 */
export const allocatableEntries = (log: ServiceTransaction[]): AllocatableEntry[] =>
    (log ?? [])
        .filter((e) => e?.id && e?.serviceId && !isPostingEntry(e) && (e.quantity || 0) > 0)
        .map((e) => ({
            id: e.id,
            serviceId: e.serviceId,
            date: e.date,
            quantity: e.quantity || 0,
        }));

/**
 * Die Protokolleintraege, auf die verzichtet wurde (ServiceItem.waivedEntryIds),
 * je Leistung — verglichen ueber `kennungFuerVergleich`, wie jede Kennung hier.
 */
export const verzichteteEintraege = (
    services: ReadonlyArray<Pick<ServiceItem, 'id' | 'waivedEntryIds'>> | null | undefined,
): Map<string, Set<string>> => {
    const out = new Map<string, Set<string>>();
    for (const service of services ?? []) {
        const ids = (service?.waivedEntryIds ?? []).map((id) => kennungFuerVergleich(id)).filter(Boolean);
        if (ids.length) out.set(service.id, new Set(ids));
    }
    return out;
};

/**
 * Das Protokoll ohne die Eintraege, auf die verzichtet wurde.
 *
 * Ein verzichteter Termin ist erbracht, wird aber nie gefordert — er darf
 * weder einer Rechnung ZUGEORDNET werden noch als abrechenbar im Zeitraum
 * zaehlen noch einen offenen Monat belegen. Bis zum 03.11.2026 kannte keiner
 * dieser Wege den Verzicht: die Menge stimmte (quantityWaived), aber eine
 * Rechnung stempelte den verzichteten Termin als abgerechnet und liess einen
 * echten offen, und der Zeitraum zog den Rest aus einem spaeteren Monat
 * (Vorfall 6172, Doppeltage im Oktober).
 *
 * AUF BEIDEN WEGEN DES TORS dieselbe Filterung: der JSON-Weg
 * (planProjectIssue) und der Journal-Weg (journalwegAmTor) reichen ihr
 * Protokoll hier durch, bevor `planAllocations` zuordnet. Filterte nur einer,
 * wichen die Zuordnungen ab und das Tor sperrte. Gesperrt wird ausschliesslich
 * ueber das LV (dieselben `services` auf beiden Seiten), nie ueber das
 * Protokoll selbst. Ohne Verzicht kommt das Protokoll unveraendert zurueck.
 */
export const ohneVerzichteteEintraege = <T extends Pick<ServiceTransaction, 'id' | 'serviceId'>>(
    log: T[],
    services: ReadonlyArray<Pick<ServiceItem, 'id' | 'waivedEntryIds'>> | null | undefined,
): T[] => {
    const verzichtet = verzichteteEintraege(services);
    if (!verzichtet.size) return log;
    return (log ?? []).filter((e) => !verzichtet.get(e?.serviceId)?.has(kennungFuerVergleich(e?.id)));
};

/**
 * Was je Protokolleintrag bereits abgerechnet ist, ueber alle Belege hinweg.
 *
 * Ein Storno traegt seine Zuordnung negativ und hebt die des Ursprungsbelegs
 * damit exakt auf — kein Loeschen, kein Entstempeln. Das ist zugleich die
 * Form, die GoBD verlangt: rueckgaengig gemacht wird durch einen Gegeneintrag.
 *
 * SCHLUESSEL ist `kennungFuerVergleich(entryId)`, nicht die rohe Kennung — und
 * wer in dieser Map nachschlaegt, schlaegt ebenso mit
 * `kennungFuerVergleich(eintrag.id)` nach. Eine im JSON grossgeschriebene uuid
 * kommt aus dem Journal kleingeschrieben zurueck; die Vorbelege nennen sie so,
 * wie sie beim Ausstellen hiess. Exakt verglichen faende der Journal-Weg ihre
 * Zuordnung nicht, hielte die Menge fuer offen und forderte sie ein zweites
 * Mal (S5a Stufe 3). Alt-Kennungen (keine uuid) bleiben exakt.
 */
export const allocatedByEntry = (invoices: Pick<Invoice, 'lines'>[]): Map<string, number> => {
    const out = new Map<string, number>();
    for (const invoice of invoices ?? []) {
        for (const line of invoice.lines ?? []) {
            for (const allocation of line.allocations ?? []) {
                const key = kennungFuerVergleich(allocation?.entryId);
                if (!key) continue;
                out.set(key, (out.get(key) ?? 0) + (allocation.quantity || 0));
            }
        }
    }
    return out;
};

/**
 * Restmenge je Eintrag; Eintraege ohne Rest fallen heraus. Nachgeschlagen
 * ueber `kennungFuerVergleich` — dieselbe Regel, mit der `allocatedByEntry`
 * die Map fuellt.
 */
export const openEntries = (
    log: ServiceTransaction[],
    allocated: Map<string, number> = new Map(),
): OpenEntry[] =>
    allocatableEntries(log)
        .map((e) => ({ ...e, openQty: e.quantity - (allocated.get(kennungFuerVergleich(e.id)) ?? 0) }))
        .filter((e) => e.openQty > EPS);

/**
 * Reihenfolge, in der Eintraege abgerechnet werden.
 *
 * Erst was in den Zeitraum des Belegs faellt, dann der Rest — innerhalb beider
 * Gruppen der aelteste zuerst. Ein Beleg behauptet einen Zeitraum; was er
 * abrechnet, soll aus diesem Zeitraum stammen. Reicht er nicht, wird
 * ausserhalb weitergefuellt, statt die Zeile ohne Zuordnung zu lassen — sonst
 * stuende im Beleg eine Menge, zu der keine Leistung benannt ist.
 */
const orderForPeriod = (
    entries: OpenEntry[],
    period?: { from?: string; to?: string },
): OpenEntry[] => {
    const inPeriod = (entry: OpenEntry): boolean => {
        if (!period?.from && !period?.to) return true;
        // Auf den Kalendertag kuerzen: das Protokoll fuehrt Zeitstempel, der
        // Zeitraum reine Tage. Ohne das gilt der letzte Tag als "danach".
        //
        // Der BERLINER Tag (`buchungstag`), nicht der UTC-Textschnitt —
        // Betreiber-Entscheid 1 vom 02.10.2026. Eine Begehung um 23:30 Uhr UTC
        // am 31.05. fand in Berlin am 01.06. statt und gehoert in den Juni.
        // Der Journal-Weg (V3, Berliner Wanduhrzeit) sieht genau diesen Tag;
        // mit dem Textschnitt lagen beide Wege an der Zeitraumkante
        // auseinander und zogen verschiedene Eintraege. Ein reiner Tag bleibt,
        // wie er ist.
        const day = buchungstag(entry.date);
        if (!day) return false;
        if (period.from && day < period.from) return false;
        if (period.to && day > period.to) return false;
        return true;
    };
    return entries.slice().sort((a, b) => {
        const inA = inPeriod(a) ? 0 : 1;
        const inB = inPeriod(b) ? 0 : 1;
        if (inA !== inB) return inA - inB;
        // Undatiertes ans Ende: es laesst sich keinem Zeitraum zuordnen und
        // soll erst herangezogen werden, wenn datierte Mengen nicht reichen.
        //
        // Sortiert auf der BERLINER Wanduhr, wie oben entschieden — nicht nach
        // dem Rohtext (Codex-Review S5a, 03.10.2026, P2): `…31T22:00:00Z`
        // (Berlin 01.06.) laege sonst vor `…31T23:30:00` ohne Zone (Berlin
        // 31.05.), und die Juni-Menge wuerde vor der Mai-Menge verbraucht.
        // Gleichstand nach der id (`kennungFuerVergleich`), nicht nach der
        // Arrayposition: das JSON liefert die Einfuegereihenfolge, das Journal
        // bei gleichem Zeitpunkt eine beliebige — derselbe Eintrag soll auf
        // beiden Wegen zuerst gezogen werden. Am Geld aendert das nichts
        // (gleiche Leistung, gleiche Minute), nur WELCHER der Zwillinge
        // abgerechnet heisst.
        const za = berlinerSortierzeit(a.date) ?? '9999-99-99';
        const zb = berlinerSortierzeit(b.date) ?? '9999-99-99';
        if (za !== zb) return za.localeCompare(zb);
        return kennungFuerVergleich(a.id).localeCompare(kennungFuerVergleich(b.id));
    });
};

/**
 * Belegt die Zeilen eines Belegs mit ihrer Zuordnung.
 *
 * Nur Leistungszeilen mit positiver Menge; Zuschlaege, Texte und Abzuege haben
 * keine Entsprechung im Leistungsprotokoll. Bleibt eine Menge uebrig, fuer die
 * es keinen offenen Eintrag gibt, wird sie NICHT erfunden — die Zeile traegt
 * dann eine unvollstaendige Zuordnung, und der Rest gilt als undatiert. Das
 * ist der reale Fall einer Menge, die direkt am Leistungsstand korrigiert und
 * nie protokolliert wurde.
 */
export const planAllocations = (
    lines: InvoiceLine[],
    log: ServiceTransaction[],
    allocated: Map<string, number> = new Map(),
    period?: { from?: string; to?: string },
    /**
     * Wie viel dieser Zeile ueberhaupt zuzuordnen ist. Voreingestellt die
     * Zeilenmenge.
     *
     * Kumulativ ist das NICHT dieselbe Zahl: die Zeile nennt den gesamten
     * Leistungsstand, gebucht und zugeordnet wird nur der Zuwachs. Ordnete man
     * die volle Menge zu, verbrauchte ein Junibeleg die Julieintraege gleich
     * mit — die Julileistung waere danach keinem Monat mehr zuzuordnen und
     * erschiene in der Uebersicht als "ohne Datum".
     */
    quantityOf?: (line: InvoiceLine) => number,
): InvoiceLine[] => {
    // Eigener Restbestand fuer diesen Lauf: mehrere Zeilen koennen dieselbe
    // Leistung betreffen (Mengensplit), und die zweite darf nicht erneut
    // zugeteilt bekommen, was die erste schon genommen hat.
    const rest = new Map<string, number>();
    const byService = new Map<string, OpenEntry[]>();
    for (const entry of openEntries(log, allocated)) {
        rest.set(entry.id, entry.openQty);
        const list = byService.get(entry.serviceId);
        if (list) list.push(entry);
        else byService.set(entry.serviceId, [entry]);
    }

    return lines.map((line) => {
        if (line.kind !== 'SERVICE' || !line.serviceId) return line;
        // Reine Betragskorrektur: Menge und Preis benennen die urspruengliche
        // Position, abgerechnet wird an ihr nichts. Ordnete man sie zu, stempelte
        // eine Berichtigung ueber 60,00 EUR die Protokolleintraege der vollen
        // Position als abgerechnet — die Leistung waere danach fuer den echten
        // Beleg verbraucht.
        if (line.quantityInformational) return line;
        let open = quantityOf ? quantityOf(line) : (line.quantity ?? 0);
        if (open <= EPS) return line;

        const allocations: InvoiceLineAllocation[] = [];
        for (const entry of orderForPeriod(byService.get(line.serviceId) ?? [], period)) {
            if (open <= EPS) break;
            const available = rest.get(entry.id) ?? 0;
            const take = Math.min(open, available);
            if (take <= EPS) continue;
            // Als Kalendertag festhalten, nicht als Zeitstempel: die Zuordnung
            // beantwortet die Frage "welcher Monat", und ein Zeitstempel wuerde
            // in jedem spaeteren Vergleich dieselbe Falle aufstellen. Der
            // Berliner Tag, aus demselben Grund wie in `orderForPeriod` — sonst
            // stuende die Begehung im Zeitraum Juni, ihre Zuordnung aber im Mai.
            // Bereits eingefrorene Zuordnungen behalten ihren Tag.
            allocations.push({ entryId: entry.id, quantity: take, date: buchungstag(entry.date) ?? undefined });
            rest.set(entry.id, available - take);
            open -= take;
        }

        return allocations.length ? { ...line, allocations } : line;
    });
};

/**
 * Dreht die Zuordnung einer Zeile um — fuer Storno und Gutschrift.
 *
 * Die gespiegelte Zeile gibt exakt das zurueck, was der Ursprungsbeleg
 * genommen hat. Ohne diese Umkehr bliebe die Leistung nach einem Storno als
 * abgerechnet stehen, waehrend quantityBilled bereits wieder gemindert wurde:
 * die Summe stimmte, aber der Zeitraumfilter faende die Leistung nie wieder.
 */
export const mirrorAllocations = (
    allocations: InvoiceLineAllocation[] | undefined,
): InvoiceLineAllocation[] | undefined =>
    allocations?.length ? allocations.map((a) => ({ ...a, quantity: -a.quantity })) : undefined;

/**
 * Der Zeitraum, den ein Beleg tatsaechlich NEU abrechnet.
 *
 * Kumulativ nennt jede Position den gesamten Leistungsstand bis zum Stichtag;
 * der ausgewiesene Leistungszeitraum reicht deshalb bis zur ersten Leistung
 * zurueck. Er beantwortet dem Kunden nicht, was an DIESER Rechnung neu ist —
 * die Frage, die er beim zweiten Beleg desselben Projekts als erstes stellt.
 * Die Zuordnung weiss es: sie traegt nur den Zuwachs, und ihre Tage spannen
 * genau den Abrechnungszeitraum auf.
 *
 * Nur Leistungszeilen: Zuschlaege, Texte und Abzuege haben keine Entsprechung
 * im Leistungsprotokoll und tragen keine Zuordnung. Das Vorzeichen bleibt
 * ausser Betracht — ein Storno spiegelt die Mengen, hebt aber die Abrechnung
 * genau derselben Tage auf und nennt deshalb denselben Zeitraum.
 *
 * Ohne Zuordnung undefined: Altbelege, reine Abschlaege und Entwuerfe haben
 * keine, und einen Zeitraum aus dem Belegdatum abzuleiten waere geraten.
 */
export const billingPeriodOf = (
    lines: InvoiceLine[] | undefined,
): { from: string; to: string } | undefined => {
    let from: string | undefined;
    let to: string | undefined;
    for (const line of lines ?? []) {
        if (line?.kind !== 'SERVICE') continue;
        for (const allocation of line.allocations ?? []) {
            // Auf den Kalendertag kuerzen: planAllocations schreibt reine Tage,
            // aber Belege aus der Zeit davor tragen hier volle Zeitstempel —
            // die ordneten sich im Vergleich als eigener, spaeterer Tag ein.
            const day = asDay(allocation?.date);
            if (!day) continue;
            if (!from || day < from) from = day;
            if (!to || day > to) to = day;
        }
    }
    return from && to ? { from, to } : undefined;
};

/** Das Wenige eines Belegs, aus dem sich der Abrechnungszeitraum ergibt. */
export interface BilledPeriodSource {
    lines?: InvoiceLine[];
    deliveryDateFrom?: string;
    deliveryDateTo?: string;
    details?: { billingMode?: 'CUMULATIVE' };
}

/**
 * Der Abrechnungszeitraum, wie ihn Beleg und Editor anzeigen — EINE Regel fuer
 * beide. Der Andruck und das, was der Bearbeiter vorher sieht, duerfen nicht
 * auseinanderlaufen; zwei Fassungen derselben Regel taeten genau das.
 *
 * Die Angabe steht auf JEDEM Beleg. Was eine Rechnung neu abrechnet, ist die
 * Frage, die der Kunde als erstes stellt — auch beim fortschreibenden Beleg,
 * wo sie sich mit dem Leistungszeitraum deckt. Die Wiederholung ist der Preis
 * dafuer, dass die Antwort nie fehlt.
 *
 * 1. Immer zuerst die Zuordnung: sie traegt nur den Zuwachs und ist beim
 *    Ausstellen eingefroren. Das gilt fuer jede Belegart — planProjectIssue
 *    ordnet auch fortschreibend zu.
 * 2. Rueckfall NUR fortschreibend: dort IST der Leistungszeitraum der
 *    Abrechnungszeitraum. Kumulativ ist derselbe Rueckfall verboten — der
 *    Leistungszeitraum reicht bis zur ersten Leistung zurueck und behauptete
 *    als Abrechnungszeitraum einen Zuwachs, den der Beleg nicht hat. Ohne
 *    Zuordnung entfaellt die Angabe dort weiterhin ersatzlos.
 * 3. Ohne jedes Datum: undefined — die Zeile entfaellt, statt zu raten.
 *
 * Ein angefangener Zeitraum ohne Ende (nur "Leistung von") ergibt keinen
 * Abrechnungszeitraum: ihn auf diesen einen Tag zu verdichten behauptete ein
 * Ende, das der Beleg nicht nennt. Bis zur Ausstellung laesst die Validierung
 * diesen Zustand ohnehin nicht durch.
 */
export const billedPeriodForDisplay = (
    invoice: BilledPeriodSource | undefined,
): { from: string; to: string } | undefined => {
    const allocated = billingPeriodOf(invoice?.lines);
    if (allocated) return allocated;
    if (invoice?.details?.billingMode === 'CUMULATIVE') return undefined;
    const from = asDay(invoice?.deliveryDateFrom);
    const to = asDay(invoice?.deliveryDateTo);
    if (!to) return undefined;
    // Nur ein Stichtag: ein Tag, kein offener Anfang.
    return { from: from && from <= to ? from : to, to };
};

/**
 * Menge, die abgerechnet ist, ohne dass ein Beleg sagt woher.
 *
 * Belege aus der Zeit vor der Zuordnung tragen keine; ihr Anteil an
 * quantityBilled laesst sich keinem Protokolleintrag zurechnen. Diese Menge zu
 * verteilen hiesse, eine Zuordnung zu erfinden — sie wird stattdessen
 * ausgewiesen und schrumpft von selbst, sobald die Altbelege storniert oder
 * die Leistungen abgeschlossen sind.
 */
export const unallocatedBilled = (
    quantityBilled: number,
    allocatedForService: number,
): number => Math.max(0, (quantityBilled || 0) - allocatedForService);

/** Summe der Zuordnungen je Leistung ueber alle Belege. */
export const allocatedByService = (invoices: Pick<Invoice, 'lines'>[]): Map<string, number> => {
    const out = new Map<string, number>();
    for (const invoice of invoices ?? []) {
        for (const line of invoice.lines ?? []) {
            if (!line.serviceId || !line.allocations?.length) continue;
            const sum = line.allocations.reduce((s, a) => s + (a.quantity || 0), 0);
            out.set(line.serviceId, (out.get(line.serviceId) ?? 0) + sum);
        }
    }
    return out;
};
